import React from 'react';
import { connect } from 'react-redux';
import Promise from 'bluebird';
import _ from 'lodash';
import { addCard } from './actions';
import AddressWrapper from 'shared/components/address/wrapper';
import withGeocoder from 'shared/components/address/geocoder';
import FormField from 'scy/FormField';
import { LoadingButton, CloseButton } from 'scy/buttons';
import { Checkbox } from 'scy/Input';
import { Warning, DangerWarning } from 'scy/Warning';
import { H5, DangerText } from 'scy/text';
import { trackNow } from 'shared/models/analytic';
import { width100 } from 'scy/utils.mod.scss';
import inputStyles from 'scy/Input/styles.mod.scss';
import {
  checkbox,
  checkboxLabel
} from 'components/checkoutPage/form/styles.mod.scss';
import margin from 'scy/margin.mod.scss';
import GTAmerica from 'fonts/scy/GT-America-Regular.woff';

let PaymentMethodForm = class PaymentMethodForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      billingIsSame:
        props.deliveryAddress &&
        props.deliveryAddress.type !== 'Business' &&
        !props.isGift,
      cardError: null,
      loading: false,
      errors: {},
      formWarning: null,
      formError: null,
      autoFocusBillingAddress: false,
      billingStreetAddress: '',
      billingZip: '',
      billingCity: '',
      billingState: '',
      billingUnit: ''
    };
  }

  componentDidMount() {
    const config =
      GTAmerica && GTAmerica.startsWith && GTAmerica.startsWith('https://')
        ? { fonts: [{ family: 'GT-America', src: `url(${GTAmerica})` }] }
        : {};
    const elements = window.stripe.elements(config);
    this.card = elements.create('card', { hidePostalCode: true });
    this.card.mount('#stripe-card-container');
    this.card.on('change', ({ error }) => {
      if (error) this.setState({ cardError: error.message });
      else this.setState({ cardError: null });
      return;
    });
    this.card.on('ready', () => this.card.focus());
  }

  toggleBillingAddress() {
    this.setState(prevState => {
      const billingIsSame = !prevState.billingIsSame;

      billingIsSame
        ? trackNow('Payment Billing Address Same Selected')
        : trackNow('Payment Billing Address Same Deselected');

      return {
        ...prevState,
        billingIsSame,
        autoFocusBillingAddress: true
      };
    });
  }

  selectAutocompleteAddress(address) {
    if (!address) return;
    this.autocompleteAddress = address;
    this.setState({
      billingStreetAddress: address.address,
      billingZip: address.zip,
      billingCity: address.city,
      billingState: address.state
    });
    setTimeout(() => this.unitInput.focus(), 250);
  }

  addressWasAutocompleted() {
    return (
      this.autocompleteAddress &&
      this.autocompleteAddress.address === this.state.billingStreetAddress
    );
  }

  zipWasAutocompleted() {
    return (
      this.autocompleteAddress &&
      this.autocompleteAddress.zip === this.state.billingZip
    );
  }

  zipLabelText() {
    return this.zipWasAutocompleted()
      ? `${this.state.billingCity}, ${this.state.billingState}`
      : 'Zip Code';
  }

  validateAddress(address) {
    if (_.isEmpty(address)) return "Billing Address can't be blank";
  }

  validateZip(zip) {
    if (_.isEmpty(zip)) return "Zip Code can't be blank";
  }

  validateFields() {
    const { billingStreetAddress, billingZip } = this.state;

    let errors = {};
    if (!this.state.billingIsSame) {
      errors.billingStreetAddress =
        this.validateAddress(billingStreetAddress) || null;
      errors.billingZip = this.validateZip(billingZip) || null;
    }

    this.setState({ errors });

    if (_.some(errors)) return Promise.reject(new Error());
    return Promise.resolve();
  }

  checkAddress() {
    if (this.state.billingIsSame)
      return Promise.resolve(this.props.deliveryAddress);

    if (this.addressWasAutocompleted() && this.zipWasAutocompleted()) {
      return Promise.resolve({
        ...this.autocompleteAddress,
        unit: this.state.billingUnit
      });
    }

    const query = [this.state.billingStreetAddress, this.state.billingZip]
      .join(' ')
      .trim();
    return this.props
      .geocode(query)
      .catch(() => {
        const error = new Error(
          'Your address could not be verified. Please check your street address and zip code, then try again.'
        );
        error.name = 'Warning';
        return Promise.reject(error);
      })
      .then(address => {
        if (address.zip !== this.state.billingZip) {
          const error = new Error(
            `The street address does not match the zip code. Do you mean ${
              address.zip
            }?`
          );
          error.name = 'Warning';
          return Promise.reject(error);
        }
        this.setState({
          billingStreetAddress: address.address,
          billingZip: address.zip,
          billingCity: address.city,
          billingState: address.state
        });
        return { ...address, unit: this.state.billingUnit };
      });
  }

  _submit(address) {
    return this.props.addCard(this.card, {
      address_line1: address.address,
      address_line2: address.unit,
      address_city: address.city,
      address_state: address.state,
      address_zip: address.zip
    });
  }

  handleSubmit(e) {
    e.preventDefault();
    this.setState({ loading: true, formWarning: null, formError: null });
    this.validateFields()
      .then(() => this.checkAddress())
      .then(address => this._submit(address))
      .then(card => this.props.onSuccess && this.props.onSuccess(card))
      .catch(error => {
        if (error.name === 'Warning') {
          this.setState({ loading: false, formWarning: error.message });
        } else if (error.name === 'CardError') {
          this.setState({ loading: false, cardError: error.message });
        } else {
          this.setState({ loading: false, formError: error.message });
        }
      });
  }

  render() {
    const FieldsContainer = this.props.fieldsContainer;

    return (
      <form onSubmit={e => this.handleSubmit(e)}>
        <FieldsContainer>
          {this.props.onCancel && <CloseButton onClick={this.props.onCancel} />}

          <div
            className={`${inputStyles.input} ${margin.bottom_sm}`}
            id="stripe-card-container"
          />

          {this.state.cardError && (
            <DangerText marginClass={`${margin.top_0} ${margin.bottom_sm}`}>
              {this.state.cardError}
            </DangerText>
          )}

          {this.props.deliveryAddress && (
            <>
              <H5 uppercase marginClass={`${margin.top_0} ${margin.bottom_xs}`}>
                Billing Address
              </H5>
              <div className={margin.bottom_sm}>
                <label htmlFor="billingAddressSame" className={checkboxLabel}>
                  <Checkbox
                    id="billingAddressSame"
                    name="billingAddressSame"
                    className={`${checkbox} ${margin.right_xs}`}
                    checked={this.state.billingIsSame}
                    onChange={() => this.toggleBillingAddress()}
                  />
                  {`Same as ${
                    this.props.shipping ? 'shipping' : 'delivery'
                  } address`}
                </label>
              </div>
            </>
          )}

          {this.state.formWarning && (
            <Warning>{this.state.formWarning}</Warning>
          )}

          {this.state.formError && (
            <DangerWarning>{this.state.formError}</DangerWarning>
          )}

          {!this.state.billingIsSame && (
            <div className="row">
              <div className="col-xs-12 col-md-8">
                <AddressWrapper
                  inputId="billing-street-address"
                  setAddress={address =>
                    this.selectAutocompleteAddress(address)
                  }
                >
                  <FormField
                    id="billing-street-address"
                    name="billing-address"
                    labelText="Billing Address"
                    initialValue={this.state.billingStreetAddress}
                    updatedValue={this.state.billingStreetAddress}
                    onChange={address =>
                      this.setState({ billingStreetAddress: address })
                    }
                    validate={address => this.validateAddress(address)}
                    error={this.state.errors.billingStreetAddress}
                    autoFocus={this.state.autoFocusBillingAddress}
                  />
                </AddressWrapper>
              </div>

              <div className="col-xs-12 col-md-4">
                <FormField
                  id="billing-zip-code"
                  name="billing-zip"
                  labelText={this.zipLabelText()}
                  initialValue={this.state.billingZip}
                  updatedValue={this.state.billingZip}
                  onChange={billingZip => this.setState({ billingZip })}
                  validate={zip => this.validateZip(zip)}
                  error={this.state.errors.billingZip}
                />
              </div>

              <div className="col-xs-12">
                <FormField
                  id="billing-unit-number"
                  labelText="Apt, suite, unit (optional)"
                  initialValue={this.state.billingUnit}
                  onChange={billingUnit => this.setState({ billingUnit })}
                  inputRef={input => (this.unitInput = input)}
                />
              </div>
            </div>
          )}
        </FieldsContainer>

        <div className="row">
          <div
            className={
              this.props.btnContainerClass ||
              'col-xs-12 col-md-5 col-md-offset-7 col-lg-4 col-lg-offset-8'
            }
          >
            <LoadingButton
              type="submit"
              className={this.props.btnClass || width100}
              disabled={this.state.cardError || this.state.loading}
              loading={this.state.loading}
            >
              Continue
            </LoadingButton>
          </div>
        </div>
      </form>
    );
  }
};

function DefaultContainer({ children }) {
  return <div>{children}</div>;
}

PaymentMethodForm.defaultProps = {
  fieldsContainer: DefaultContainer
};

PaymentMethodForm = withGeocoder(PaymentMethodForm);

PaymentMethodForm = connect(
  state => ({
    isGift: state.checkout.isGift,
    shipping: !!state.checkout.shippingOption
  }),
  { addCard }
)(PaymentMethodForm);

export default PaymentMethodForm;
